/**
 * A stateful stream formatter for terminal output from MicroPython/CircuitPython devices.
 *
 * Processes incoming text and injects ANSI escape codes for:
 * - Tracebacks: red text with blue underlined file links
 * - REPL prompts (>>>): bold magenta
 * - Boot banners (MicroPython v... / Adafruit Circuit...): dim grey
 *
 * The formatter processes complete lines (delimited by \n). Partial lines are
 * passed through unmodified to preserve xterm cursor behavior.
 */

/**
 * Internal state — tracks whether we're currently inside a traceback block.
 */
type FormatterState = 'normal' | 'traceback';

export class TerminalFormatter {
    private state: FormatterState = 'normal';
    private pendingLine = '';

    /**
     * Process a chunk of incoming text and return formatted output ready for xterm.write().
     *
     * Complete lines (\n-terminated) are styled. Partial lines at the end of
     * a chunk are also styled (for prompt coloring) but \r characters are
     * preserved as-is for xterm cursor handling.
     */
    format(text: string): string {
        // Split into lines, keeping the delimiters
        const combined = this.pendingLine + text;
        const lines = combined.split('\n');

        // Last element is the incomplete line (no trailing \n)
        this.pendingLine = lines.pop() || '';

        let output = '';

        // Process each complete line
        for (const line of lines) {
            output += this.formatLine(line) + '\n';
        }

        // Format the pending partial line too (for prompt coloring)
        if (this.pendingLine.length > 0) {
            output += this.formatPartial(this.pendingLine);
            this.pendingLine = '';
        }

        return output;
    }

    /**
     * Reset the formatter state. Call this when the terminal is cleared or reset.
     */
    reset(): void {
        this.state = 'normal';
        this.pendingLine = '';
    }

    // --- Private ---

    private formatLine(line: string): string {
        // Strip \r for pattern matching (xterm handles \r itself)
        const stripped = line.replace(/\r$/, '');

        // Detect traceback start
        if (stripped.includes('Traceback (most recent call last):')) {
            this.state = 'traceback';
            return ANSI.red + line;
        }

        // Inside a traceback
        if (this.state === 'traceback') {
            if (isErrorLine(stripped)) {
                this.state = 'normal';
                return line + ANSI.reset;
            }

            // File reference — clickable blue underlined link
            if (stripped.includes('File "') && stripped.includes(', line ')) {
                return line.replace(
                    /(File "([^"]+)", line (\d+))/g,
                    ANSI.blueUnderline + '$1' + ANSI.resetUnderline + ANSI.red,
                );
            }

            // Other traceback lines stay red (color set by traceback start)
            return line;
        }

        // Normal mode styling
        if (stripped.match(/^>>>/)) {
            return ANSI.boldMagenta + line + ANSI.reset;
        }

        if (
            stripped.startsWith('MicroPython v') ||
            stripped.startsWith('Adafruit CircuitPython')
        ) {
            return ANSI.dim + line + ANSI.reset;
        }

        if (
            stripped.startsWith('Type "help()"') ||
            stripped.startsWith('Press any key')
        ) {
            return ANSI.dim + line + ANSI.reset;
        }

        return line;
    }

    /**
     * Format a partial line (no trailing \n yet). Used for prompt coloring.
     * Preserves any leading \r for proper cursor positioning.
     */
    private formatPartial(partial: string): string {
        // Extract leading \r characters — they must come before any ANSI codes
        // so xterm moves the cursor to column 0 first
        let prefix = '';
        let content = partial;
        while (content.startsWith('\r')) {
            prefix += '\r';
            content = content.substring(1);
        }

        if (content.match(/^>>>/)) {
            return prefix + ANSI.boldMagenta + content + ANSI.reset;
        }

        if (content.match(/^\.\.\./)) {
            return prefix + ANSI.boldMagenta + content + ANSI.reset;
        }

        if (this.state === 'traceback') {
            return prefix + content; // Already in red context
        }

        return partial;
    }
}

// --- Helpers ---

/**
 * Detect the final line of a traceback (e.g., "ValueError: invalid syntax").
 */
function isErrorLine(line: string): boolean {
    return /^[A-Z][A-Za-z]*(?:Error|Exception|Interrupt)(?::|$)/.test(
        line.trimStart(),
    );
}

/**
 * ANSI escape code constants.
 */
const ANSI = {
    red: '\x1b[31m',
    blueUnderline: '\x1b[94m\x1b[4m',
    resetUnderline: '\x1b[24m',
    boldMagenta: '\x1b[95m\x1b[1m',
    dim: '\x1b[2m',
    reset: '\x1b[0m',
} as const;
